import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { Resources } from "Infrastructure/Translations/Resources";
import type { ObjPathProxy } from "ts-object-path";
import { type ObjectSchema, bool, date, object, string } from "yup";
import { number } from "../Utils/schemaUtils";

export type ChildrenGoalFormSchema = {
  readonly referenceStartDate: Date;
  readonly includeEarlyAge: boolean;
  readonly includePrimarySchool: boolean;
  readonly includeHighSchool: boolean;
  readonly includeUniversity: boolean;
  readonly includeStartToLife: boolean;
  readonly earlyAgeAmount?: number;
  readonly earlyAgeWithdrawalIn?: number;
  readonly primarySchoolAmount?: number;
  readonly primarySchoolWithdrawalIn?: number;
  readonly highSchoolAmount?: number;
  readonly highSchoolWithdrawalIn?: number;
  readonly universityAmount?: number;
  readonly universityWithdrawalIn?: number;
  readonly startToLifeAmount?: number;
  readonly startToLifeWithdrawalIn?: number;
  readonly monthlyContributionWithFee: number;
  readonly shortTermInterestRate: number;
  readonly midTermInterestRate: number;
  readonly longTermInterestRate: number;
  readonly inflationRate: number;
  readonly feeRate: number;
  readonly includeInflation?: boolean;
  readonly includeFee?: boolean;
  readonly note?: string;
  readonly priority?: boolean;
};

export const getChildrenGoalFormSchema: (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
) => ObjectSchema<ChildrenGoalFormSchema> = (t) => {
  return object({
    includeEarlyAge: bool().required(),
    includePrimarySchool: bool().required(),
    includeHighSchool: bool().required(),
    includeUniversity: bool().required(),
    includeStartToLife: bool().required(),
    referenceStartDate: date().required(t(Resources.Validation.Required)),
    earlyAgeAmount: number().when("includeEarlyAge", (includeEarlyAge, schema) => {
      return includeEarlyAge[0]
        ? schema
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .min(1, t(Resources.Validation.Number.MoreThen, { count: 0 }))
            .required(t(Resources.Validation.Required))
        : schema;
    }),
    earlyAgeWithdrawalIn: number().when("includeEarlyAge", (includeEarlyAge, schema) => {
      return includeEarlyAge[0] ? schema.required(t(Resources.Validation.Required)) : schema;
    }),
    primarySchoolAmount: number().when("includePrimarySchool", (includeEarlyAge, schema) => {
      return includeEarlyAge[0]
        ? schema
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .min(1, t(Resources.Validation.Number.MoreThen, { count: 0 }))
            .required(t(Resources.Validation.Required))
        : schema;
    }),
    primarySchoolWithdrawalIn: number().when("includePrimarySchool", (includeEarlyAge, schema) => {
      return includeEarlyAge[0] ? schema.required(t(Resources.Validation.Required)) : schema;
    }),
    highSchoolAmount: number().when("includeHighSchool", (includeEarlyAge, schema) => {
      return includeEarlyAge[0]
        ? schema
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .min(1, t(Resources.Validation.Number.MoreThen, { count: 0 }))
            .required(t(Resources.Validation.Required))
        : schema;
    }),
    highSchoolWithdrawalIn: number().when("includeHighSchool", (includeEarlyAge, schema) => {
      return includeEarlyAge[0] ? schema.required(t(Resources.Validation.Required)) : schema;
    }),
    universityAmount: number().when("includeUniversity", (includeEarlyAge, schema) => {
      return includeEarlyAge[0]
        ? schema
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .min(1, t(Resources.Validation.Number.MoreThen, { count: 0 }))
            .required(t(Resources.Validation.Required))
        : schema;
    }),
    universityWithdrawalIn: number().when("includeUniversity", (includeEarlyAge, schema) => {
      return includeEarlyAge[0] ? schema.required(t(Resources.Validation.Required)) : schema;
    }),
    startToLifeAmount: number().when("includeStartToLife", (includeEarlyAge, schema) => {
      return includeEarlyAge[0]
        ? schema
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .min(1, t(Resources.Validation.Number.MoreThen, { count: 0 }))
            .required(t(Resources.Validation.Required))
        : schema;
    }),
    startToLifeWithdrawalIn: number().when("includeStartToLife", (includeEarlyAge, schema) => {
      return includeEarlyAge[0] ? schema.required(t(Resources.Validation.Required)) : schema;
    }),
    monthlyContributionWithFee: number().required(t(Resources.Validation.Required)),
    shortTermInterestRate: number().required(),
    midTermInterestRate: number().required(),
    longTermInterestRate: number().required(),
    inflationRate: number().required(),
    feeRate: number().required(t(Resources.Validation.Required)),
    includeInflation: bool().required(t(Resources.Validation.Required)),
    includeFee: bool().required(t(Resources.Validation.Required)),
    note: string().max(NOTE_MAX_LENGTH, t(Resources.Validation.String.Max, { count: NOTE_MAX_LENGTH })),
    priority: bool(),
  }).defined();
};
